import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Archive, Trash2, GitMerge, X, ArrowLeft } from "lucide-react";
import { pluralUnit } from "./unitLabel";
import { ArchivedItemsPanel } from "./ArchivedItemsPanel";
import { UnitTypeSelect } from "./UnitTypeSelect";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ManageItemListPanel = ({ items, authHeaders, onRefresh, isOwner, storageAreas, month, year, purchaseRefreshKey }) => {
  const [adding, setAdding] = useState(false);
  const [viewingArchived, setViewingArchived] = useState(false);
  const [newName, setNewName] = useState("");
  const [newArea, setNewArea] = useState("pantry");
  const [newUnit, setNewUnit] = useState("bag");
  const [newYieldQty, setNewYieldQty] = useState("");
  const [newYieldChildren, setNewYieldChildren] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [mergeSelection, setMergeSelection] = useState([]); // up to 2 item ids, checked directly in the table
  const [mergeKeepId, setMergeKeepId] = useState(null); // which of the 2 selected the director explicitly chose to keep
  const [addedThisMonth, setAddedThisMonth] = useState({});
  const [archiveConfirm, setArchiveConfirm] = useState(null); // { item, dishes } — set when the item is linked to a dish this month

  useEffect(() => {
    if (!month || !year) return;
    axios.get(`${API}/inventory/purchases`, { params: { month, year }, headers: authHeaders })
      .then((res) => setAddedThisMonth(res.data.addedThisMonthByItem || {}))
      .catch((err) => console.error("Error fetching added-this-month totals:", err));
  }, [month, year, authHeaders, purchaseRefreshKey]);

  const areas = storageAreas || [];
  const areaLabel = (key) => areas.find((a) => a.key === key)?.label || key;

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/inventory/items`, {
        canonicalName: newName.trim(), storageArea: newArea, unitType: newUnit,
        servingYieldQty: newYieldQty === "" ? null : parseFloat(newYieldQty),
        servingYieldChildren: newYieldChildren === "" ? null : parseInt(newYieldChildren, 10),
      }, { headers: authHeaders });
      toast.success("Item added");
      setNewName(""); setNewYieldQty(""); setNewYieldChildren(""); setAdding(false);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add item");
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditDraft({
      canonicalName: item.canonicalName, packageSize: item.packageSize || "",
      storageArea: item.storageArea, unitType: item.unitType,
      servingYieldQty: item.servingYieldQty ?? "", servingYieldChildren: item.servingYieldChildren ?? "",
    });
  };

  const handleSaveEdit = async (itemId) => {
    if (!editDraft.canonicalName?.trim()) return;
    try {
      await axios.put(`${API}/inventory/items/${itemId}`, {
        canonicalName: editDraft.canonicalName.trim(),
        packageSize: editDraft.packageSize?.trim() || null,
        storageArea: editDraft.storageArea, unitType: editDraft.unitType,
        servingYieldQty: editDraft.servingYieldQty === "" ? null : parseFloat(editDraft.servingYieldQty),
        servingYieldChildren: editDraft.servingYieldChildren === "" ? null : parseInt(editDraft.servingYieldChildren, 10),
      }, { headers: authHeaders });
      toast.success("Item updated");
      setEditingId(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update item");
    }
  };

  const handleArchiveClick = async (item) => {
    try {
      const res = await axios.get(`${API}/inventory/items/${item.id}/menu-links`, { params: { month, year }, headers: authHeaders });
      const dishes = res.data.dishes || [];
      if (dishes.length > 0) {
        setArchiveConfirm({ item, dishes });
      } else {
        await doArchive(item.id);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to check item");
    }
  };

  const doArchive = async (itemId) => {
    try {
      await axios.put(`${API}/inventory/items/${itemId}/deactivate`, {}, { headers: authHeaders });
      toast.success("Item moved to Archived");
      setMergeSelection((sel) => sel.filter((id) => id !== itemId));
      setArchiveConfirm(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to archive item");
      setArchiveConfirm(null);
    }
  };

  const handleRemoveAlias = async (itemId, alias) => {
    try {
      await axios.delete(`${API}/inventory/items/${itemId}/aliases`, { headers: authHeaders, data: { alias } });
      toast.success("Alias removed");
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove alias");
    }
  };

  const toggleMergeSelection = (itemId) => {
    setMergeKeepId(null); // re-pick "which to keep" whenever the pair changes
    setMergeSelection((sel) => {
      if (sel.includes(itemId)) return sel.filter((id) => id !== itemId);
      if (sel.length >= 2) return sel; // exactly 2 at a time — untick one first
      return [...sel, itemId];
    });
  };

  const clearMergeSelection = () => { setMergeSelection([]); setMergeKeepId(null); };

  const confirmMerge = async () => {
    if (!mergeKeepId || mergeSelection.length !== 2) return;
    const sourceId = mergeSelection.find((id) => id !== mergeKeepId);
    try {
      await axios.post(`${API}/inventory/items/${mergeKeepId}/merge`, { sourceItemId: sourceId }, { headers: authHeaders });
      toast.success("Items merged");
      clearMergeSelection();
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to merge items");
      clearMergeSelection();
    }
  };

  const mergeSelectedItems = mergeSelection.map((id) => items.find((i) => i.id === id)).filter(Boolean);
  const activeItems = items.filter((i) => i.active);
  const archivedItems = items.filter((i) => !i.active);

  if (viewingArchived) {
    return (
      <div data-testid="manage-item-list-panel" className="space-y-4">
        <button data-testid="back-to-item-list-button" onClick={() => setViewingArchived(false)}
          className="flex items-center gap-1.5 text-sm text-[#2F6DB5] hover:underline font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to Item List
        </button>
        <ArchivedItemsPanel items={items} authHeaders={authHeaders} onRefresh={onRefresh} isOwner={isOwner} />
      </div>
    );
  }

  return (
    <div data-testid="manage-item-list-panel" className="space-y-4">
      <p data-testid="master-item-list-note" className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
        Your full catalog for every menu. Adding an item here doesn't connect it to this month — it updates the full inventory for future scanned menus. For something needed this month, use '+ Add Item' on This Month's Menu Items instead.
      </p>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {activeItems.length} active item{activeItems.length !== 1 ? "s" : ""} on file. Aliases shown under each item.
          {isOwner && " Check any 2 duplicate items below to merge them."}
        </p>
        <div className="flex items-center gap-2">
          {isOwner && (
            <Button data-testid="view-archived-button" size="sm" variant="outline" onClick={() => setViewingArchived(true)}>
              <Archive className="w-4 h-4 mr-1" /> Archived ({archivedItems.length})
            </Button>
          )}
          {isOwner && (
            <Button data-testid="add-item-button" size="sm" onClick={() => setAdding((v) => !v)}>
              <Plus className="w-4 h-4 mr-1" /> Add Item
            </Button>
          )}
        </div>
      </div>

      {mergeSelection.length === 1 && (
        <p className="text-xs text-slate-500" data-testid="merge-hint-one-selected">
          Check one more item to merge "{mergeSelectedItems[0]?.canonicalName}" with.
        </p>
      )}

      {mergeSelection.length === 2 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="merge-panel">
          {!mergeKeepId ? (
            <>
              <p className="text-sm text-slate-700 flex items-center gap-1.5">
                <GitMerge className="w-4 h-4 text-amber-600" />
                Merge items — choose which one you want as your main item. The other will be sent to Archived, listed as merged.
              </p>
              <div className="flex flex-wrap gap-2">
                {mergeSelectedItems.map((it) => (
                  <Button key={it.id} variant="outline" size="sm" data-testid={`merge-keep-${it.id}`}
                    onClick={() => setMergeKeepId(it.id)}>
                    Use "{it.canonicalName}" as main item
                  </Button>
                ))}
                <Button variant="ghost" size="sm" data-testid="merge-cancel" onClick={clearMergeSelection}>Cancel</Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-amber-800" data-testid="merge-summary">
                "{mergeSelectedItems.find((it) => it.id !== mergeKeepId)?.canonicalName}" will be merged into
                "{mergeSelectedItems.find((it) => it.id === mergeKeepId)?.canonicalName}" and sent to
                Archived, listed as merged. This can't be undone.
              </p>
              <div className="flex gap-2">
                <Button size="sm" data-testid="merge-confirm-ok" onClick={confirmMerge}>Merge</Button>
                <Button size="sm" variant="ghost" data-testid="merge-confirm-cancel" onClick={clearMergeSelection}>Cancel</Button>
              </div>
            </>
          )}
        </div>
      )}

      {adding && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <Input data-testid="new-item-name-input" placeholder="Item name" value={newName}
            onChange={(e) => setNewName(e.target.value)} className="w-48" />
          <Select value={newArea} onValueChange={setNewArea}>
            <SelectTrigger className="w-40" data-testid="new-item-storage-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {areas.map((a) => (
                <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <UnitTypeSelect value={newUnit} onChange={setNewUnit} testId="new-item-unit-select" triggerClassName="w-28" />
          <Input data-testid="new-item-yield-qty-input" placeholder="Qty" type="number" min="0" value={newYieldQty}
            onChange={(e) => setNewYieldQty(e.target.value)} className="w-20" />
          <span className="text-sm text-slate-500 self-center">serves</span>
          <Input data-testid="new-item-yield-children-input" placeholder="Children" type="number" min="0" value={newYieldChildren}
            onChange={(e) => setNewYieldChildren(e.target.value)} className="w-24" />
          <Button data-testid="confirm-add-item-button" size="sm" onClick={handleAdd}>Save</Button>
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Package Size</th>
              <th className="text-left px-3 py-2">Storage Area</th>
              <th className="text-left px-3 py-2">Package Type</th>
              <th className="text-left px-3 py-2">Serving Yield</th>
              <th className="text-left px-3 py-2">Added This Month</th>
              {isOwner && <th className="text-left px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {activeItems.map((item) => {
              const isEditing = editingId === item.id;
              return (
                <tr key={item.id} data-testid={`item-row-${item.id}`} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <Input value={editDraft.canonicalName}
                        onChange={(e) => setEditDraft((d) => ({ ...d, canonicalName: e.target.value }))}
                        className="h-8 w-40" data-testid={`edit-name-input-${item.id}`} />
                    ) : (
                      <>
                        <span className="font-medium text-slate-800">{item.canonicalName}</span>
                        {item.aliases?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {item.aliases.map((alias) => (
                              <span key={alias} data-testid={`alias-chip-${item.id}-${alias}`}
                                className="text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-0.5 flex items-center gap-1">
                                {alias}
                                {isOwner && (
                                  <button data-testid={`remove-alias-${item.id}-${alias}`} title="Remove alias"
                                    onClick={() => handleRemoveAlias(item.id, alias)}>
                                    <X className="w-3 h-3 hover:text-red-500" />
                                  </button>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isEditing ? (
                      <Input value={editDraft.packageSize}
                        onChange={(e) => setEditDraft((d) => ({ ...d, packageSize: e.target.value }))}
                        placeholder="e.g. 5 lb bag" className="h-8 w-32" data-testid={`edit-package-size-${item.id}`} />
                    ) : (item.packageSize || "—")}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {isEditing ? (
                      <Select value={editDraft.storageArea} onValueChange={(v) => setEditDraft((d) => ({ ...d, storageArea: v }))}>
                        <SelectTrigger className="h-8 w-40" data-testid={`edit-storage-area-${item.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {areas.map((a) => (
                            <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : areaLabel(item.storageArea)}
                  </td>
                  <td className="px-3 py-2 capitalize text-slate-600">
                    {isEditing ? (
                      <UnitTypeSelect value={editDraft.unitType}
                        onChange={(v) => setEditDraft((d) => ({ ...d, unitType: v }))}
                        testId={`edit-unit-type-${item.id}`} triggerClassName="h-8 w-24" />
                    ) : item.unitType}
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <Input value={editDraft.servingYieldQty} type="number" min="0"
                          onChange={(e) => setEditDraft((d) => ({ ...d, servingYieldQty: e.target.value }))}
                          placeholder="Qty" className="h-8 w-16" data-testid={`edit-yield-qty-${item.id}`} />
                        <span className="text-xs">serves</span>
                        <Input value={editDraft.servingYieldChildren} type="number" min="0"
                          onChange={(e) => setEditDraft((d) => ({ ...d, servingYieldChildren: e.target.value }))}
                          placeholder="Children" className="h-8 w-20" data-testid={`edit-yield-children-${item.id}`} />
                      </div>
                    ) : item.servingYieldQty && item.servingYieldChildren ? (
                      <span data-testid={`serving-yield-${item.id}`}>
                        {item.servingYieldQty} {pluralUnit(item.unitType, item.servingYieldQty)} serves {item.servingYieldChildren} children
                      </span>
                    ) : (
                      <span className="text-slate-400">Not set</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-600" data-testid={`added-this-month-${item.id}`}>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">+{addedThisMonth[item.id] || 0}</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          View details in the Invoices & Receipts tab.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </td>
                  {isOwner && (
                    <td className="px-3 py-2">
                      {isEditing ? (
                        <div className="flex gap-2">
                          <Button size="sm" className="h-8" data-testid={`save-edit-${item.id}`} onClick={() => handleSaveEdit(item.id)}>Save</Button>
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setEditingId(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button data-testid={`rename-item-${item.id}`} title="Edit item" onClick={() => startEdit(item)}>
                            <Pencil className="w-4 h-4 text-slate-500 hover:text-[#2F6DB5]" />
                          </button>
                          <button data-testid={`archive-item-${item.id}`} title="Move to Archived — to permanently delete, go to the Archived area"
                            onClick={() => handleArchiveClick(item)}>
                            <Trash2 className="w-4 h-4 text-slate-500 hover:text-red-500" />
                          </button>
                          <input type="checkbox" title="Select to merge with another item"
                            data-testid={`merge-checkbox-${item.id}`}
                            checked={mergeSelection.includes(item.id)}
                            disabled={!mergeSelection.includes(item.id) && mergeSelection.length >= 2}
                            onChange={() => toggleMergeSelection(item.id)}
                            className="w-4 h-4 accent-amber-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40" />
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!archiveConfirm} onOpenChange={(open) => !open && setArchiveConfirm(null)}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move "{archiveConfirm?.item?.canonicalName}" to Archived?</AlertDialogTitle>
            <AlertDialogDescription>
              This item is used in: {archiveConfirm?.dishes?.join(", ")}. Moving it to Archived will remove it from
              This Month's Menu Items — it won't be counted or added to your Shopping List this month.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="archive-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="archive-confirm-ok" onClick={() => doArchive(archiveConfirm.item.id)}>
              Move to Archived
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageItemListPanel;
